import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminReferralPayouts() {
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [newPayout, setNewPayout] = useState({
    referral_source_id: '',
    period_start: '',
    period_end: ''
  });
  const queryClient = useQueryClient();

  const { data: payouts } = useQuery({
    queryKey: ['referral-payouts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_payouts')
        .select(`
          *,
          referral_sources (name, email, referral_code),
          payout_line_items (
            id,
            amount,
            referral_events (
              referral_types (display_name)
            )
          )
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    }
  });

  const { data: sources } = useQuery({
    queryKey: ['referral-sources-for-payout'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_sources')
        .select('*')
        .eq('is_active', true);
      if (error) throw error;
      return data;
    }
  });

  const generatePayoutMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('generate_payout_batch', {
        p_referral_source_id: newPayout.referral_source_id,
        p_period_start: newPayout.period_start,
        p_period_end: newPayout.period_end
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Payout generated!');
      setIsGenerateModalOpen(false);
      setNewPayout({ referral_source_id: '', period_start: '', period_end: '' });
      queryClient.invalidateQueries({ queryKey: ['referral-payouts'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to generate payout');
    }
  });

  const markAsPaidMutation = useMutation({
    mutationFn: async ({ id, payment_reference }: { id: string; payment_reference: string }) => {
      const { error } = await supabase
        .from('referral_payouts')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
          payment_reference
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Payout marked as paid!');
      queryClient.invalidateQueries({ queryKey: ['referral-payouts'] });
    },
    onError: () => {
      toast.error('Failed to update payout');
    }
  });

  const statusColors = {
    pending: 'bg-yellow-500',
    processing: 'bg-blue-500',
    paid: 'bg-green-500',
    failed: 'bg-red-500'
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Referral Payouts</h1>
        <Button onClick={() => setIsGenerateModalOpen(true)}>
          <DollarSign className="w-4 h-4 mr-2" /> Generate Payout
        </Button>
      </div>

      <div className="grid gap-4">
        {payouts?.map((payout) => (
          <Card key={payout.id} className="p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={statusColors[payout.status as keyof typeof statusColors]}>
                    {payout.status}
                  </Badge>
                  <h3 className="text-lg font-semibold">
                    {payout.referral_sources?.name}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Email: {payout.referral_sources?.email}
                </p>
                <p className="text-sm text-muted-foreground">
                  Code: {payout.referral_sources?.referral_code}
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-lg font-bold text-primary">
                    <DollarSign className="w-5 h-5" />
                    <span>{payout.total_amount}</span>
                  </div>
                </div>
                {payout.payment_reference && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Reference: {payout.payment_reference}
                  </p>
                )}
                {payout.paid_at && (
                  <p className="text-sm text-muted-foreground">
                    Paid: {new Date(payout.paid_at).toLocaleDateString()}
                  </p>
                )}
                <div className="mt-4 border-t pt-3">
                  <h4 className="text-sm font-medium mb-2">Line Items ({payout.payout_line_items?.length || 0}):</h4>
                  <div className="space-y-1">
                    {payout.payout_line_items?.slice(0, 3).map((item: any) => (
                      <div key={item.id} className="text-sm text-muted-foreground">
                        {item.referral_events?.referral_types?.display_name}: ${Number(item.amount).toFixed(2)}
                      </div>
                    ))}
                    {(payout.payout_line_items?.length || 0) > 3 && (
                      <div className="text-sm text-muted-foreground">
                        + {(payout.payout_line_items?.length || 0) - 3} more items
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {payout.status === 'pending' && (
                <Button
                  onClick={() => {
                    const ref = prompt('Enter payment reference (e.g., PayPal transaction ID):');
                    if (ref) {
                      markAsPaidMutation.mutate({ id: payout.id, payment_reference: ref });
                    }
                  }}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark as Paid
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Generate Payout Dialog */}
      <Dialog open={isGenerateModalOpen} onOpenChange={setIsGenerateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Payout Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Referral Source</Label>
              <Select
                value={newPayout.referral_source_id}
                onValueChange={(v) => setNewPayout({ ...newPayout, referral_source_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {sources?.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.referral_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Period Start</Label>
              <Input
                type="date"
                value={newPayout.period_start}
                onChange={(e) => setNewPayout({ ...newPayout, period_start: e.target.value })}
              />
            </div>
            <div>
              <Label>Period End</Label>
              <Input
                type="date"
                value={newPayout.period_end}
                onChange={(e) => setNewPayout({ ...newPayout, period_end: e.target.value })}
              />
            </div>
            <Button 
              onClick={() => generatePayoutMutation.mutate()} 
              className="w-full"
              disabled={!newPayout.referral_source_id || !newPayout.period_start || !newPayout.period_end}
            >
              Generate Payout
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
